import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { CssUiComponent } from '@webapp/common/components/survey-custom';
import { Textfield, TextfieldType } from '@webapp/common/components/textfield';
import { setHtml } from '@webapp/common/lib/ui';

import { selectIsWithAudioRecogn } from '@webapp/survey/src/machine/selectors';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import type { MatrixCell } from '../matrix-layout/matrix-cell';
import cssLayout from '../matrix-layout/matrix-layout.css';

import css from './matrix-text.css';

const imaskOptions = {
    thousandsSeparator: ' ',
    scale: 4,
};

export const MatrixTextGroup: VFC<MatrixCell> = ({
    answer,
    group: groupActor,
    height,
    question,
    disabled,
    onChange,
}) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const isWithAudioRecogn = useStateSelector(selectIsWithAudioRecogn);
    const group = useSelector(groupActor, (g) => g.context);
    const { inputStyle, textStyle } = useTextStyle();
    const responseActor = group.responses[answer.id];
    const response = useSelector(responseActor, (r) => r.context);
    const value = (response?.value ?? '') as string;
    const { answerType } = question.params;
    const numeric = answerType === 'number';

    const handleChangeValue = useCallback(
        (value: string) => {
            if (disabled) {
                onChange?.();
            }
            groupActor.send({ type: 'CHANGE_RESPONSE_VALUE', answerId: answer.id, value });
        },
        [groupActor, answer, disabled],
    );

    return (
        <div className={cn(CssUiComponent.CELL, cssLayout.cell, css.answer)} style={{ height }}>
            <div className={css.field}>
                <div
                    className={cn(CssUiComponent.LABEL, cssLayout.cell, cssLayout.rowHeaderMobile, css.name)}
                    style={textStyle}
                    {...setHtml(pipingAnswersReplacer(group.name))}
                />
                <div className={css.text}>
                    <Textfield
                        withSpeechRecognition={isWithAudioRecogn}
                        transparent
                        style={inputStyle}
                        type={numeric ? TextfieldType.IMASK_IRRATIONAL : undefined}
                        imaskOptions={numeric ? imaskOptions : undefined}
                        value={disabled ? '' : value}
                        onChange={handleChangeValue}
                    />
                </div>
            </div>
        </div>
    );
};
MatrixTextGroup.displayName = 'Group';
